import React from 'react';
import { X } from 'lucide-react';
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  UserCheck, 
  Megaphone, 
  MessageCircle, 
  BarChart3, 
  Shield, 
  Settings,
  TrendingUp,
  Rocket,
  Ticket
} from 'lucide-react';
import { AdminSpotlightSection } from './AdminSpotlightSection';
import { AdminSectionsGrid, AdminSectionData } from './AdminSectionsGrid';

interface AdminMegaMenuProps {
  onClose: () => void;
  onNavigationClick?: (href: string) => void;
  onUserActivity?: () => void;
}

const adminOperations: AdminSectionData[] = [
  { 
    label: 'User Management', 
    href: '/admin?tab=users', 
    icon: Users,
    description: 'Manage user accounts, permissions, and access control'
  },
  { 
    label: 'Company Management', 
    href: '/admin?tab=companies', 
    icon: Building2,
    description: 'Organisation settings and business entity management'
  },
  { 
    label: 'CRM Dashboard', 
    href: '/admin?tab=crm', 
    icon: UserCheck,
    description: 'Customer relationship management and lead tracking'
  },
  { 
    label: 'Marketing Hub', 
    href: '/admin?tab=marketing', 
    icon: Megaphone,
    description: 'Campaign management, automation, and performance analytics'
  },
  { 
    label: 'WhatsApp Business', 
    href: '/admin?tab=whatsapp', 
    icon: MessageCircle,
    description: 'WhatsApp integration and business communication'
  },
  { 
    label: 'Deployment Center', 
    href: '/admin?tab=deployment', 
    icon: Rocket,
    description: 'Release management, CI/CD, and infrastructure deployment'
  },
  { 
    label: 'User Adoption', 
    href: '/admin?tab=user-adoption', 
    icon: TrendingUp,
    description: 'Feature usage analytics and engagement metrics'
  },
  { 
    label: 'Business Analytics', 
    href: '/admin?tab=analytics', 
    icon: BarChart3,
    description: 'Advanced analytics, reports, and business intelligence'
  },
  { 
    label: 'Security Center', 
    href: '/admin?tab=security', 
    icon: Shield,
    description: 'Security monitoring, audit logs, and threat detection'
  },
  { 
    label: 'System Settings', 
    href: '/admin?tab=settings', 
    icon: Settings,
    description: 'System configuration, preferences, and global settings'
  },
  { 
    label: 'Support Tickets', 
    href: '/admin?tab=ticketing', 
    icon: Ticket,
    description: 'Multi-tenant ticketing system with SLA management'
  }
];

export const AdminMegaMenu: React.FC<AdminMegaMenuProps> = ({ onClose, onNavigationClick, onUserActivity }) => {
  const handleLinkClick = (href: string, e: React.MouseEvent) => {
    e.preventDefault();
    
    if (onNavigationClick) {
      onNavigationClick(href);
    }
    onClose();
  };

  const handleUserActivity = () => {
    if (onUserActivity) {
      onUserActivity();
    }
  };

  return (
    <div
      className="relative bg-white shadow-xl border-t border-l border-r border-b border-gray-100 z-[160] w-full max-w-5xl mx-auto overflow-hidden pointer-events-auto"
      style={{
        minWidth: 360
      }}
      tabIndex={0}
      role="dialog"
      aria-label="Admin menu"
      onMouseMove={handleUserActivity}
      onClick={handleUserActivity}
      onKeyDown={handleUserActivity}
      onTouchStart={handleUserActivity}
    >
      {/* Close button in top-right */}
      <button
        aria-label="Close Admin Menu"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 bg-gray-100/80 hover:bg-gray-200 focus:bg-gray-300 p-1.5 transition-all duration-150 shadow-sm border border-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        tabIndex={0}
        type="button"
      >
        <X className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
      </button>

      <div className="flex flex-col md:flex-row">
        {/* Left spotlight area */}
        <AdminSpotlightSection onLinkClick={handleLinkClick} />

        {/* Right side - Admin sections */}
        <div className="flex-[2] px-6 py-4 bg-white/90">
          <div className="mb-4">
            <h3 className="text-xl font-semibold text-gray-900 tracking-tight mb-1">Administration Center</h3>
            <p className="text-gray-500 text-sm">Comprehensive system management and analytics</p>
          </div>
          
          <AdminSectionsGrid sections={adminOperations} onLinkClick={handleLinkClick} />
          
          {/* Quick Actions Footer */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex items-center mb-2">
              <span className="text-xs font-semibold text-muted-foreground tracking-widest uppercase mr-2">Quick Actions</span>
              <span className="flex-1 border-t border-dashed border-gray-200" />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={(e) => handleLinkClick('/admin?tab=security', e)}
                className="text-sm text-blue-600 hover:text-blue-700 transition-colors inline-flex items-center"
              >
                <Shield className="w-3 h-3 mr-1" />
                Security Center →
              </button>
              <button
                onClick={(e) => handleLinkClick('/admin?tab=settings', e)}
                className="text-sm text-blue-600 hover:text-blue-700 transition-colors inline-flex items-center"
              >
                <Settings className="w-3 h-3 mr-1" />
                System Settings →
              </button>
              <div className="flex items-center gap-2 text-xs text-muted-foreground ml-auto">
                <Shield className="w-3 h-3" />
                <span>Secure Environment</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};